/**
 * pathway — a scroll-linked organic thread connecting N waypoint elements
 * down a page, the visible claim that each station causes the next. One
 * continuous Catmull-Rom spline through every waypoint (never separate
 * arrows), cut into per-span `<path>`s so each span can carry its own
 * scroll-driven reveal while the curve stays visually continuous across the
 * joins. Waypoints resolve from live `getBoundingClientRect()`, so the
 * geometry — including any breakpoint-driven column flip a waypoint's own
 * layout performs — falls out of measurement, never a breakpoint branch
 * here.
 *
 * Cross-shadow reach: a string waypoint resolves via `discover()`
 * (@schmancy/mixins) — a synchronous recursive walk of the document,
 * descending into every open shadow root, the same mechanism `hummingbird`
 * uses to reach into sibling custom elements' shadow roots. The first
 * discovery pass is still gated behind one `requestAnimationFrame`: `discover()`
 * itself answers synchronously off whatever DOM exists at call time, but
 * Lit's first render of a just-connected element is a microtask, not inline
 * in its `connectedCallback`; walking in the same tick the waypoints mount
 * finds every shadow root still empty. One rAF (fires after the microtask
 * queue drains) is enough to land after that first paint — the same gate
 * `art.directive.ts` uses before its own first `getBoundingClientRect`.
 *
 * Scroll progress samples a single capture-phase `fromEvent(document,
 * 'scroll')` via `auditTime(0, animationFrameScheduler)` — no RAF loop —
 * gated behind an `IntersectionObserver`, mirroring `art.directive.ts`'s
 * pause discipline so an off-screen thread costs nothing.
 */

import { directive, type ElementPart, PartType } from 'lit/directive.js'
import { AsyncDirective } from 'lit/async-directive.js'
import { noChange } from 'lit'
import { animationFrameScheduler, forkJoin, fromEvent, of, Subject, Subscription } from 'rxjs'
import { auditTime, take, takeUntil } from 'rxjs/operators'
import { discover } from '@schmancy/mixins/discovery.service'
import { catmullRomSpline, type CurvePoint } from '@schmancy/utils/curves'
import { reducedMotion$ } from './reduced-motion'

export interface PathwayOptions {
	/** Top-to-bottom waypoints. A string resolves cross-shadow via `discover()`; an `HTMLElement` is used directly. */
	waypoints: (string | HTMLElement)[]
	/** Draw the small terminal "and back to the top" glyph past the last waypoint, once its span finishes drawing. */
	closesLoop?: boolean
}

interface Span {
	reveal: SVGPathElement
	total: number
}

interface PathwayState {
	host: HTMLElement
	svg: SVGSVGElement
	waypointEls: HTMLElement[]
	spans: Span[]
	loopGlyph: SVGGElement | null
	closesLoop: boolean
	ember: SVGGElement | null
	emberPulse: Animation | null
	io?: IntersectionObserver
	ro?: ResizeObserver
	scrollSub?: Subscription
	teardown$: Subject<void>
}

const SVG_NS = 'http://www.w3.org/2000/svg'
const SPAN_SAMPLES = 16

let instanceCounter = 0

class PathwayDirective extends AsyncDirective {
	private state: PathwayState | null = null
	private readonly instanceId = ++instanceCounter

	render(_options: PathwayOptions) {
		return noChange
	}

	override update(part: ElementPart, [options]: [PathwayOptions]) {
		if (part.type !== PartType.ELEMENT) {
			throw new Error('pathway directive must be used on an element')
		}
		const host = part.element as HTMLElement

		if (this.state) return noChange

		const svg = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement
		svg.setAttribute('aria-hidden', 'true')
		host.appendChild(svg)

		this.state = {
			host,
			svg,
			waypointEls: [],
			spans: [],
			loopGlyph: null,
			closesLoop: !!options.closesLoop,
			ember: null,
			emberPulse: null,
			teardown$: new Subject<void>(),
		}

		requestAnimationFrame(() => {
			if (!this.state) return
			forkJoin(options.waypoints.map(w => (typeof w === 'string' ? discover<HTMLElement>(w) : of(w))))
				.pipe(take(1))
				.subscribe(resolved => {
					if (!this.state) return
					this.state.waypointEls = resolved.filter((el): el is HTMLElement => el !== null)
					if (this.state.waypointEls.length < 2) return

					this.rebuildGeometry()
					this.setupVisibilityObserver()
					this.state.ro = new ResizeObserver(() => this.rebuildGeometry())
					this.state.ro.observe(this.state.host)
					reducedMotion$.pipe(takeUntil(this.state.teardown$)).subscribe(() => this.applyMotionMode())
				})
		})

		return noChange
	}

	private rebuildGeometry(): void {
		if (!this.state) return
		const { host, svg, waypointEls } = this.state

		const hostRect = host.getBoundingClientRect()
		svg.setAttribute('width', String(hostRect.width))
		svg.setAttribute('height', String(hostRect.height))
		svg.setAttribute('viewBox', `0 0 ${hostRect.width} ${hostRect.height}`)
		svg.replaceChildren()

		const points: CurvePoint[] = waypointEls.map(el => {
			const r = el.getBoundingClientRect()
			return { x: r.left - hostRect.left, y: r.top - hostRect.top }
		})

		// The guide is the whole route, printed once, faint and always fully
		// drawn — a board laid out before a single piece has moved on it. It
		// carries its own token (outline-variant) rather than the host's
		// currentColor, so it reads as backdrop against the reveal layer above it.
		const guideGroup = document.createElementNS(SVG_NS, 'g')
		guideGroup.setAttribute('class', 'text-outline-variant')
		const revealGroup = document.createElementNS(SVG_NS, 'g')
		const dotGroup = document.createElementNS(SVG_NS, 'g')

		const spanCount = points.length - 1
		const spans: Span[] = []
		for (let i = 0; i < spanCount; i++) {
			const p0 = points[Math.max(0, i - 1)]
			const p1 = points[i]
			const p2 = points[Math.min(points.length - 1, i + 1)]
			const p3 = points[Math.min(points.length - 1, i + 2)]
			const samples: CurvePoint[] = [p1]
			for (let step = 1; step <= SPAN_SAMPLES; step++) {
				samples.push(catmullRomSpline(p0, p1, p2, p3, step / SPAN_SAMPLES))
			}
			const d = samples.map((p, j) => `${j === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')

			const guide = document.createElementNS(SVG_NS, 'path')
			guide.setAttribute('d', d)
			guide.setAttribute('fill', 'none')
			guide.setAttribute('stroke', 'currentColor')
			guide.setAttribute('stroke-width', '1.25')
			guide.setAttribute('stroke-linecap', 'round')
			guide.setAttribute('opacity', '0.28')
			guideGroup.appendChild(guide)

			// The substance changes span-over-span: stroke width and opacity
			// increase monotonically — a want (thin, translucent) becoming a
			// commitment becoming cash (bold, opaque) by the last station. This
			// IS the meaning, encoded as a property of the ink, not a caption.
			const ratio = spanCount === 1 ? 1 : i / (spanCount - 1)
			const reveal = document.createElementNS(SVG_NS, 'path')
			reveal.setAttribute('d', d)
			reveal.setAttribute('fill', 'none')
			reveal.setAttribute('stroke', 'currentColor')
			reveal.setAttribute('stroke-width', String(2 + ratio * 2.5))
			reveal.setAttribute('stroke-linecap', 'round')
			reveal.setAttribute('opacity', String(0.65 + ratio * 0.35))
			revealGroup.appendChild(reveal)

			const total = reveal.getTotalLength()
			reveal.setAttribute('stroke-dasharray', String(total))
			reveal.style.strokeDashoffset = String(total)

			spans.push({ reveal, total })
		}

		for (const p of points) {
			const halo = document.createElementNS(SVG_NS, 'circle')
			halo.setAttribute('cx', String(p.x))
			halo.setAttribute('cy', String(p.y))
			halo.setAttribute('r', '8')
			halo.setAttribute('fill', 'currentColor')
			halo.setAttribute('opacity', '0.18')
			dotGroup.appendChild(halo)
			const dot = document.createElementNS(SVG_NS, 'circle')
			dot.setAttribute('cx', String(p.x))
			dot.setAttribute('cy', String(p.y))
			dot.setAttribute('r', '3.5')
			dot.setAttribute('fill', 'currentColor')
			dotGroup.appendChild(dot)
		}

		svg.appendChild(guideGroup)
		svg.appendChild(revealGroup)
		svg.appendChild(dotGroup)
		this.state.spans = spans

		// The ember — a small traveling glow riding the exact tip of whichever
		// span is mid-reveal: the thread isn't just a progress bar, something is
		// journeying it. paintProgress() positions and shows/hides it every
		// sampled tick; it lives only while a span is genuinely between
		// waypoints (0 < progress < 1).
		this.state.emberPulse?.cancel()
		const ember = document.createElementNS(SVG_NS, 'g') as SVGGElement
		ember.style.opacity = '0'
		const blurFilterId = `pathway-ember-blur-${this.instanceId}`
		const emberHalo = document.createElementNS(SVG_NS, 'circle')
		emberHalo.setAttribute('r', '9')
		emberHalo.setAttribute('fill', 'currentColor')
		emberHalo.setAttribute('opacity', '0.35')
		emberHalo.setAttribute('filter', `url(#${blurFilterId})`)
		const emberCore = document.createElementNS(SVG_NS, 'circle')
		emberCore.setAttribute('r', '3')
		emberCore.setAttribute('fill', 'currentColor')
		ember.appendChild(emberHalo)
		ember.appendChild(emberCore)
		svg.appendChild(ember)
		this.state.ember = ember
		this.state.emberPulse = null

		const defs = document.createElementNS(SVG_NS, 'defs')
		const filter = document.createElementNS(SVG_NS, 'filter')
		filter.setAttribute('id', blurFilterId)
		filter.setAttribute('x', '-200%')
		filter.setAttribute('y', '-200%')
		filter.setAttribute('width', '500%')
		filter.setAttribute('height', '500%')
		const blur = document.createElementNS(SVG_NS, 'feGaussianBlur')
		blur.setAttribute('stdDeviation', '3')
		filter.appendChild(blur)
		defs.appendChild(filter)
		svg.prepend(defs)

		// The terminal "and back to the top" glyph — a small hand-drawn hook,
		// not a second waypoint-derived span, sitting just past the last
		// station. Acknowledges the cycle without fabricating a route back up.
		let loopGlyph: SVGGElement | null = null
		if (this.state.closesLoop && points.length > 0) {
			const last = points[points.length - 1]
			loopGlyph = document.createElementNS(SVG_NS, 'g') as SVGGElement
			loopGlyph.setAttribute('transform', `translate(${last.x - 4}, ${last.y + 44})`)
			loopGlyph.style.opacity = '0'
			loopGlyph.style.transition = 'opacity 400ms ease'
			const hook = document.createElementNS(SVG_NS, 'path')
			hook.setAttribute('d', 'M 18 10 A 11 11 0 1 1 4 -4')
			hook.setAttribute('fill', 'none')
			hook.setAttribute('stroke', 'currentColor')
			hook.setAttribute('stroke-width', '2')
			hook.setAttribute('stroke-linecap', 'round')
			const arrow = document.createElementNS(SVG_NS, 'path')
			arrow.setAttribute('d', 'M 4 -4 L -1 3 M 4 -4 L 11 -3')
			arrow.setAttribute('fill', 'none')
			arrow.setAttribute('stroke', 'currentColor')
			arrow.setAttribute('stroke-width', '2')
			arrow.setAttribute('stroke-linecap', 'round')
			loopGlyph.appendChild(hook)
			loopGlyph.appendChild(arrow)
			svg.appendChild(loopGlyph)
		}
		this.state.loopGlyph = loopGlyph

		this.applyMotionMode()
	}

	private setupVisibilityObserver(): void {
		if (!this.state || typeof IntersectionObserver === 'undefined') return
		this.state.io = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) this.startScrollTracking()
				else this.stopScrollTracking()
			},
			{ rootMargin: '200px' },
		)
		this.state.io.observe(this.state.host)
	}

	/**
	 * Every span renders fully drawn, immediately, statically under reduced
	 * motion — no dash animation, no ember (a thing whose whole job is
	 * implying motion has nothing false to imply once motion is off), and the
	 * loop glyph shows at full opacity. The thread degrades to a complete
	 * legible map, never loses meaning.
	 */
	private applyMotionMode(): void {
		if (!this.state) return
		if (reducedMotion$.value) {
			this.stopScrollTracking()
			for (const span of this.state.spans) span.reveal.style.strokeDashoffset = '0'
			if (this.state.loopGlyph) this.state.loopGlyph.style.opacity = this.state.closesLoop ? '1' : '0'
			if (this.state.ember) this.state.ember.style.opacity = '0'
			this.state.emberPulse?.cancel()
			this.state.emberPulse = null
		} else {
			for (const span of this.state.spans) span.reveal.style.strokeDashoffset = String(span.total)
			if (this.state.loopGlyph) this.state.loopGlyph.style.opacity = '0'
			this.startScrollTracking()
		}
	}

	private startScrollTracking(): void {
		if (!this.state || this.state.scrollSub || reducedMotion$.value) return
		this.state.scrollSub = fromEvent(document, 'scroll', { capture: true, passive: true })
			.pipe(auditTime(0, animationFrameScheduler), takeUntil(this.state.teardown$))
			.subscribe(() => this.paintProgress())
		this.paintProgress()
	}

	private stopScrollTracking(): void {
		this.state?.scrollSub?.unsubscribe()
		if (!this.state) return
		this.state.scrollSub = undefined
		this.state.emberPulse?.cancel()
		this.state.emberPulse = null
		if (this.state.ember) this.state.ember.style.opacity = '0'
	}

	private paintProgress(): void {
		if (!this.state) return
		const { waypointEls, spans, loopGlyph, closesLoop, ember } = this.state
		const refY = window.innerHeight * 0.4
		let atTip: { path: SVGPathElement; total: number; progress: number } | null = null

		for (let i = 0; i < spans.length; i++) {
			const topY = waypointEls[i].getBoundingClientRect().top
			const botY = waypointEls[i + 1].getBoundingClientRect().top
			const progress = Math.min(1, Math.max(0, (refY - topY) / (botY - topY || 1)))
			spans[i].reveal.style.strokeDashoffset = String(spans[i].total * (1 - progress))
			if (progress > 0 && progress < 1) atTip = { path: spans[i].reveal, total: spans[i].total, progress }
			if (i === spans.length - 1 && loopGlyph) {
				loopGlyph.style.opacity = closesLoop ? String(Math.min(1, Math.max(0, (progress - 0.7) / 0.3))) : '0'
			}
		}

		if (!ember) return
		if (!atTip) {
			ember.style.opacity = '0'
			this.state.emberPulse?.cancel()
			this.state.emberPulse = null
			return
		}
		const tip = atTip.path.getPointAtLength(atTip.total * atTip.progress)
		ember.style.transform = `translate(${tip.x.toFixed(1)}px, ${tip.y.toFixed(1)}px)`
		ember.style.opacity = '1'
		// The pulse never touches position — it scales/fades the halo child
		// around the parent <g>'s own translate, so a mid-pulse scroll tick can
		// update position every frame without fighting a running Web Animation
		// (the same halo/core separation gravity.ts uses for directive-owned motion).
		if (!this.state.emberPulse && !reducedMotion$.value) {
			const halo = ember.firstElementChild as SVGCircleElement
			this.state.emberPulse = halo.animate(
				[
					{ opacity: 0.3, transform: 'scale(1)' },
					{ opacity: 0.6, transform: 'scale(1.35)' },
					{ opacity: 0.3, transform: 'scale(1)' },
				],
				{ duration: 1400, iterations: Infinity, easing: 'ease-in-out' },
			)
		}
	}

	override disconnected(): void {
		if (!this.state) return
		this.state.teardown$.next()
		this.state.io?.disconnect()
		this.state.ro?.disconnect()
		this.state.emberPulse?.cancel()
		this.state.svg.remove()
		this.state = null
	}
}

export const pathway = directive(PathwayDirective)
